import React, { useEffect, useRef } from 'react';
import CSelect from 'src/components/form/Select';
import { Spacer } from 'src/common';
import { useAssessmentQuery, useAssessmentsListQuery, useProfileQuery } from 'src/graphql/_generated';
import { AutoForm } from '../AutoForm';
import { FormField } from '../FormField';
import { SettingsFormProps } from '../registry';
import { Hint } from '../../styled';

type Opt = { label: string; value: string };

// Settings form for the expected-loss component. Picks an assessment (the
// component's data source) with the app-standard CSelect, fetches its
// base/live/projected expected loss via Apollo and embeds the figures into
// attrs.data — the FE provides all data; the runner just renders it. sourceId =
// the assessment UxId, kept for the future runner-side fetch. Config fields
// (which/label/emphasis) use the shared AutoForm.
export const ExpectedLossSettingsForm: React.FC<SettingsFormProps> = ({ descriptor, value, onChange }) => {
  const { data: profile } = useProfileQuery();
  const workspaceId = profile?.profile?.activeWorkspace?.id;
  const currency = profile?.profile?.activeWorkspace?.currency ?? null;

  const { data: listData } = useAssessmentsListQuery({ variables: { workspaceId }, skip: !workspaceId });
  const assessmentOptions: Opt[] = (listData?.assessments ?? []).map((a) => ({ label: a.name, value: a.id }));

  const selectedId = value.sourceId;
  const { data: assessmentData, loading } = useAssessmentQuery({
    variables: { id: selectedId ?? '' },
    skip: !selectedId,
  });

  // Latest envelope in a ref so the data-sync effect never depends on `value`
  // (which changes every keystroke) and so we don't loop.
  const valueRef = useRef(value);
  valueRef.current = value;

  // When the picked assessment's figures arrive, embed them into attrs.data.
  useEffect(() => {
    if (!selectedId) return;
    const a = assessmentData?.assessment;
    if (!a) return;
    const next = {
      base: a.AssessmentBaseRisk?.expectedLoss ?? null,
      live: a.AssessmentLiveRisk?.expectedLoss ?? null,
      projected: a.AssessmentProjectedRisk?.expectedLoss ?? null,
      currency,
      assessmentName: a.name ?? null,
    };
    if (JSON.stringify(next) !== JSON.stringify(valueRef.current.data)) {
      onChange({ ...valueRef.current, data: next });
    }
  }, [assessmentData, selectedId, currency, onChange]);

  const sourceHint = !workspaceId
    ? 'Loading workspace…'
    : selectedId
      ? loading
        ? 'Fetching expected loss…'
        : 'Expected-loss figures embedded from this assessment.'
      : 'Pick an assessment to embed its expected-loss figures.';

  return (
    <>
      <FormField label="Assessment" required>
        <CSelect
          inputId="assessment"
          name="assessment"
          placeholder="Pick an assessment"
          isDisabled={!workspaceId}
          options={assessmentOptions}
          value={assessmentOptions.find((o) => o.value === selectedId) ?? null}
          onChange={(o: Opt) => onChange({ ...valueRef.current, sourceId: o.value, data: null })}
        />
        <Spacer $px={6} />
        <Hint>{sourceHint}</Hint>
      </FormField>

      <AutoForm
        schema={descriptor.configSchema}
        value={value.config}
        onChange={(config) => onChange({ ...valueRef.current, config })}
      />
    </>
  );
};
